import React, { useEffect, useRef, useState } from 'react';
import { Location, LocationCategory } from '../types';
import { Navigation, Search, Crosshair } from 'lucide-react';
import toast from 'react-hot-toast';

interface MapProps {
  center: [number, number];
  onClick?: (lat: number, lng: number) => void;
  withSearch?: boolean;
  locations?: Location[];
  onLocationClick?: (location: Location) => void;
  height?: string;
  showUserLocation?: boolean;
}

const categoryConfig: Record<LocationCategory, { color: string; label: string }> = {
  'eco-trail': { color: 'green', label: 'Экасцежка' },
  'camping': { color: 'brown', label: 'Кемпінг' },
  'stargazing': { color: 'darkBlue', label: 'Назіранне за зоркамі' },
  'picnic': { color: 'orange', label: 'Пікнік' },
  'eco-hotel': { color: 'violet', label: 'Экаатэль' },
  'estate': { color: 'yellow', label: 'Сядзіба' },
  'zoo': { color: 'red', label: 'Звярынцы' },
  'historical': { color: 'blue', label: 'Гістарычныя месцы' },
  'other': { color: 'gray', label: 'Іншае' }
};

export const Map: React.FC<MapProps> = ({ 
  center, 
  onClick, 
  withSearch, 
  locations = [],
  onLocationClick,
  height = '400px',
  showUserLocation = false
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<ymaps.Map | null>(null);
  const placemarkRef = useRef<ymaps.Placemark | null>(null);
  const userPlacemarkRef = useRef<ymaps.Placemark | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [coordinates, setCoordinates] = useState<string>(`${center[0]}, ${center[1]}`);
  const markersRef = useRef<ymaps.Placemark[]>([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  const BELARUS_BOUNDS = [
    [51.2621, 23.1783],
    [56.1726, 32.7627]
  ];

  const clearMarkers = () => {
    if (mapInstanceRef.current) {
      markersRef.current.forEach(marker => {
        mapInstanceRef.current?.geoObjects.remove(marker);
      });
      markersRef.current = [];
    }
  };

  const updateUserLocationMarker = (coords: [number, number]) => {
    if (!mapInstanceRef.current) return;

    try {
      if (userPlacemarkRef.current) {
        userPlacemarkRef.current.geometry?.setCoordinates(coords);
      } else {
        userPlacemarkRef.current = new window.ymaps.Placemark(coords, {
          hintContent: 'Ваша месцазнаходжанне'
        }, {
          preset: 'islands#geolocationIcon'
        });
        mapInstanceRef.current.geoObjects.add(userPlacemarkRef.current);
      }
    } catch (error) {
      console.error('Error updating user location marker:', error);
    }
  };

  const handleMyLocationClick = () => {
    if (!navigator.geolocation) {
      toast.error('Геалакацыя не падтрымліваецца вашым браўзерам');
      return;
    }

    toast.loading('Вызначэнне месцазнаходжання...', { id: 'geolocation' });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords: [number, number] = [position.coords.latitude, position.coords.longitude];
        
        if (coords[0] < BELARUS_BOUNDS[0][0] || coords[0] > BELARUS_BOUNDS[1][0] ||
            coords[1] < BELARUS_BOUNDS[0][1] || coords[1] > BELARUS_BOUNDS[1][1]) {
          toast.error('Ваша месцазнаходжанне за межамі Беларусі', { id: 'geolocation' });
          return;
        }

        setUserLocation(coords);
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(coords, 14);
          updateUserLocationMarker(coords);
          
          if (onClick) {
            onClick(coords[0], coords[1]);
            updateMarkerPosition(coords);
          }
        }

        toast.success('Месцазнаходжанне вызначана', { id: 'geolocation' });
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Не атрымалася вызначыць месцазнаходжанне';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Доступ да геалакацыі забаронены';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Інфармацыя аб месцазнаходжанні недаступная';
            break;
          case error.TIMEOUT:
            errorMessage = 'Час чакання вызначэння месцазнаходжання скончыўся';
            break;
        }
        
        toast.error(errorMessage, { id: 'geolocation' });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const addLocationMarkers = () => {
    if (!mapInstanceRef.current) return;

    clearMarkers();

    if (showUserLocation && userLocation) {
      updateUserLocationMarker(userLocation);
    }

    locations.forEach(location => {
      try {
        const categoryStyle = categoryConfig[location.category];
        const marker = new window.ymaps.Placemark(
          [location.coordinates.lat, location.coordinates.lng],
          {
            hintContent: location.name,
            balloonContent: `
              <div class="p-4">
                <h3 class="font-semibold mb-2">${location.name}</h3>
                <p class="text-sm text-gray-600">${location.shortDescription}</p>
              </div>
            `
          },
          {
            preset: `islands#${categoryStyle.color}DotIcon`,
            draggable: false
          }
        );

        marker.events.add('click', () => {
          if (onLocationClick) {
            onLocationClick(location);
          }
        });

        mapInstanceRef.current?.geoObjects.add(marker);
        markersRef.current.push(marker);
      } catch (error) {
        console.error('Error adding location marker:', error);
      }
    });
  };

  const updateMarkerPosition = (coords: [number, number]) => {
    if (!mapInstanceRef.current) return;

    try {
      if (placemarkRef.current) {
        placemarkRef.current.geometry?.setCoordinates(coords);
      } else {
        placemarkRef.current = new window.ymaps.Placemark(coords, {
          hintContent: 'Абраная лакацыя'
        }, {
          preset: 'islands#redDotIcon',
          draggable: Boolean(onClick)
        });
        mapInstanceRef.current.geoObjects.add(placemarkRef.current);

        if (onClick) {
          placemarkRef.current.events.add('dragend', () => {
            const newCoords = placemarkRef.current?.geometry?.getCoordinates();
            if (newCoords) {
              onClick(newCoords[0], newCoords[1]);
              setCoordinates(`${newCoords[0]}, ${newCoords[1]}`);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error updating marker position:', error);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    try {
      const map = new window.ymaps.Map(mapRef.current, {
        center: center,
        zoom: onClick ? 13 : 14,
        controls: ['zoomControl'],
        restrictMapArea: BELARUS_BOUNDS
      });

      mapInstanceRef.current = map;
      
      if (onClick) {
        updateMarkerPosition(center);
        
        map.events.add('click', (e: { get: (arg0: string) => [number, number]; }) => {
          const coords = e.get('coords');
          onClick(coords[0], coords[1]);
          setCoordinates(`${coords[0]}, ${coords[1]}`);
          updateMarkerPosition(coords);
        });
      } else if (locations.length === 0) {
        if (showUserLocation) {
          handleMyLocationClick();
        } else {
          updateMarkerPosition(center);
        }
      } else {
        addLocationMarkers();
      }

      setIsMapReady(true);
    } catch (error) {
      console.error('Error initializing map:', error);
      toast.error('Не атрымалася ініцыялізаваць карту');
    }
  };

  useEffect(() => {
    if (window.ymaps) {
      window.ymaps.ready(() => {
        initializeMap();
      });
    }

    return () => {
      if (mapInstanceRef.current) {
        clearMarkers();
        if (userPlacemarkRef.current) {
          mapInstanceRef.current.geoObjects.remove(userPlacemarkRef.current);
          userPlacemarkRef.current = null;
        }
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        placemarkRef.current = null;
        setIsMapReady(false);
      }
    };
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current && isMapReady) {
      if (!onClick && locations.length > 0) {
        addLocationMarkers();
      } else if (!onClick) {
        if (showUserLocation && userLocation) {
          updateUserLocationMarker(userLocation);
        } else {
          updateMarkerPosition(center);
        }
      }
    }
  }, [locations, isMapReady, userLocation]);

  useEffect(() => {
    if (mapInstanceRef.current && isMapReady) {
      mapInstanceRef.current.setCenter(center);
      if (showUserLocation && userLocation) {
        updateUserLocationMarker(userLocation);
      } else {
        updateMarkerPosition(center);
      }
      setCoordinates(`${center[0]}, ${center[1]}`);
    }
  }, [center[0], center[1], isMapReady]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!mapInstanceRef.current || !searchQuery.trim()) return;

    try {
      const results = await window.ymaps.geocode(searchQuery);
      const firstGeoObject = results.geoObjects.get(0);
      
      if (firstGeoObject) {
        const coords = firstGeoObject.geometry.getCoordinates();
        mapInstanceRef.current.setCenter(coords);
        mapInstanceRef.current.setZoom(14);
        
        if (onClick) {
          onClick(coords[0], coords[1]);
          setCoordinates(`${coords[0]}, ${coords[1]}`);
          updateMarkerPosition(coords);
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Не атрымалася знайсці месца');
    }
  };

  const handleCoordinatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCoordinates(e.target.value);
  };

  const handleCoordinatesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const [lat, lng] = coordinates.split(',').map(coord => parseFloat(coord.trim()));
    if (!isNaN(lat) && !isNaN(lng)) {
      if (onClick) {
        onClick(lat, lng);
      }
      updateMarkerPosition([lat, lng]);
      mapInstanceRef.current?.setCenter([lat, lng]);
      mapInstanceRef.current?.setZoom(14);
    }
  };

  if (!withSearch) {
    return (
      <div className="relative h-full">
        <div 
          ref={mapRef} 
          className="w-full h-full rounded-lg overflow-hidden"
          style={{ height }}
        />
        <button
          onClick={handleMyLocationClick}
          className="absolute bottom-4 right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors z-10"
          title="Маё месцазнаходжанне"
        >
          <Crosshair size={20} className="text-primary" />
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="space-y-2 p-4 bg-white rounded-t-xl">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              placeholder="Пошук па адрасе..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
            />
          </div>
          <button
            onClick={handleSearch}
            type="button"
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          >
            <Search size={20} />
            <span className="hidden sm:inline">Знайсці</span>
          </button>
          <button
            onClick={handleMyLocationClick}
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            title="Маё месцазнаходжанне"
          >
            <Crosshair size={20} className="text-primary" />
          </button>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={coordinates}
            onChange={handleCoordinatesChange}
            onKeyPress={(e) => e.key === 'Enter' && handleCoordinatesSubmit(e)}
            placeholder="Каардынаты (шырата, даўгата)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
          />
          <button
            onClick={handleCoordinatesSubmit}
            type="button"
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
          >
            <Navigation size={20} />
            <span className="hidden sm:inline">Усталяваць</span>
          </button>
        </div>
      </div>

      <div className="relative flex-1">
        <div ref={mapRef} className="absolute inset-0" />
      </div>
    </div>
  );
};